import React, { useContext, useEffect, useState } from 'react'

import {
  AppTheme,
  Box,
  Card,
  CardStrip,
  Collapse,
  Grid,
  Typography,
  useAppTheme,
  makeAppStyles,
  ClassNameMap,
  PercentageBlob,
  RoundedPlainTextButton,
  ProviderLogo,
  Divider,
  Chip,
  SecondaryIconTooltip,
  RoundedPlainTextButtonMenu,
  Health,
  BackdropLoader,
} from '@percept/mui'

import { Add, ArrowDropDown, ArrowDropUp, CheckOutlined, Close, WarningOutlined } from '@percept/mui/icons'

import { DSLRenderer } from './DSLComponents'

import { InsightOutputUnitDetail } from './InsightOutputUnitDetail'

import {
  InsightUnitsByImportance,
  useFilteredMetricMetadata,
  useReportSeriesInsightUnitScores,
  useReportSeriesReportEntity,
  useReportSeriesReportPayload,
} from '@percept/hooks'

import {
  InsightImportance,
  DimensionType,
  InsightOutputUnit,
  InsightVertical,
  Nullable,
  PlatformUnit,
  ReportParams,
  ReportProvider,
  ReportProviderV2,
  SeriesParams,
  InsightCategory,
} from '@percept/types'

import { every, flatten, get, intersection, some, sum, xor } from 'lodash-es'

import { avg, coerceReportProvider } from '@percept/utils'

import { MetricDetailDialog } from './MetricDetailDialog'

import { automaticPrecisionPercentageFormatter, percentageFormatter } from '@percept/mui/charts'
import { InsightContext, InsightUrlParams } from '../../contexts'
import { getInsightCategoryLabel, getInsightCategoryOrder, getInsightVerticalLabel } from './lib'


type InsightUnitClassName = (
  'sectionToolbar' | 'sectionIcon' | 'sectionHealth' | 'sectionToolbarChip' | 'sectionToolbarChipIcon' |
  'sectionExplainer' | 'sectionExplainerIcon' | 'card' | 'cardContent' | 'titleContent' | 'textContent' |
  'title' | 'text' | 'chip' | 'chipWrapper' | 'verticalChip' | 'categoryChip'
)

const useInsightUnitStyles = makeAppStyles<{}, InsightUnitClassName>( theme => ({
  sectionToolbar: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    cursor: 'pointer',
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
  },
  sectionHealth: {
    marginLeft: theme.spacing(2),
    fontFamily: theme.typography.fontFamily,
    fontSize: 18,
    fontWeight: theme.typography.fontWeightBold,
  },
  sectionExplainer: {
    display: 'flex',
    marginLeft: 'auto',
    marginRight: theme.spacing(1),
    fontSize: 'inherit',
  },
  sectionExplainerIcon: {
    fontSize: '18px important!',
  },
  card: {
    minHeight: '100%',
    cursor: 'pointer',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(1.5),
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  chipWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  sectionToolbarChip: {
    color: 'inherit',
    borderColor: 'inherit',
  },
  sectionToolbarChipIcon: {
    color: 'inherit',
  },
  chip: {
    marginRight: theme.spacing(1),
    height: 20,
    fontSize: 12,
    padding: 4,
    '&:last-of-type': {
      marginRight: 0,
    },
  },
  categoryChip: {
    color: theme.palette.text.secondary,
  },
  verticalChip: {
    color: (
      theme.palette.type === 'dark' ?
        theme.palette.secondary.light :
        theme.palette.secondary.main
    ),
    borderColor: (
      theme.palette.type === 'dark' ?
        theme.palette.secondary.light :
        theme.palette.secondary.main
    ),
  },
  textContent: {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
    justifyContent: 'center',
    marginLeft: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  text: {
    fontSize: 14,
    fontWeight: theme.typography.fontWeightMedium,
  }
}))


const InsightUnitComponent = ({
  insightUnit,
  appTheme,
  classes,
  onClick,
}: {
  insightUnit: InsightOutputUnit
  appTheme: AppTheme
  classes: ClassNameMap<InsightUnitClassName>
  onClick: () => void
}): JSX.Element => {
  const { health } = insightUnit
  const healthColor = (
    health !== null ?
      appTheme.chart.healthColourScale(health) :
      null
  )
  const contrastColor = healthColor ? appTheme.palette.getContrastText(healthColor) : undefined
  return (
    <Card className={classes.card} onClick={onClick}>
      <CardStrip color='health' health={health} />
      <div className={classes.cardContent}>
        <div className={classes.titleContent}>
          { healthColor && health !== null && (
            <PercentageBlob
              value={health * 100}
              size={36}
              fontSize={12}
              color={healthColor}
              textColor={contrastColor} />
          )}
          <Typography variant='h6' className={classes.title}>
            { insightUnit.title }
          </Typography>
        </div>
        <div className={classes.textContent}>
          <div className={classes.text}>
            <DSLRenderer
              text={insightUnit.text}
              showRecommendations={false}
              summariseAnaylsisMetadata={true}
              showEntityCountChip={false}
              currency={insightUnit.currency}
              appTheme={appTheme} />
          </div>
          { !!(insightUnit.category || insightUnit.vertical || insightUnit.effective_potential_efficiency_rate !== null) && (
            <div className={classes.chipWrapper}>
              { insightUnit.vertical && (
                <Chip
                  classes={{root: `${classes.chip} ${classes.verticalChip}`}}
                  variant='outlined'
                  color='secondary'
                  size='small'
                  label={getInsightVerticalLabel(insightUnit.vertical)} />
              )}
              { insightUnit.category && (
                <Chip
                  classes={{root: `${classes.chip} ${classes.categoryChip}`}}
                  variant='outlined'
                  color='default'
                  size='small'
                  label={getInsightCategoryLabel(insightUnit.category)} />
              )}
              {/* { insightUnit.effective_potential_efficiency_rate !== null && (
                <Chip
                  classes={{root: classes.chip}}
                  variant='outlined'
                  icon={
                    insightUnit.effective_potential_efficiency_rate ?
                      <WarningOutlined /> :
                      <CheckOutlined /> 
                  }
                  color='secondary'
                  size='small'
                  label={
                    `Wastage ${automaticPrecisionPercentageFormatter(insightUnit.effective_potential_efficiency_rate * 100)}`
                  } />
              )} */}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}


const CollapsibleInsightUnits = ({
  title,
  explainer,
  collapsed,
  toggleCollapse,
  insightUnits,
  onInsightUnitClick,
  appTheme,
  classes,
}: {
  title: React.ReactNode
  explainer?: React.ReactNode
  collapsed: boolean
  toggleCollapse: () => void
  onInsightUnitClick: (insightUnit: InsightOutputUnit) => void
  insightUnits: InsightOutputUnit[]
  appTheme: AppTheme
  classes: ClassNameMap<InsightUnitClassName>
}): JSX.Element => {
  const healthValues: number[] = insightUnits.map( i => i.health ).filter( h => h !== null )
  const overallHealth = healthValues.length ? avg(healthValues) : null
  const backgroundColor = (
    overallHealth === null ?
      appTheme.palette.background.paper :
      appTheme.chart.healthColourScale(overallHealth)
  )
  const textColor = appTheme.palette.getContrastText(backgroundColor)
  const displayHealth = overallHealth === null ? null : (
    percentageFormatter(Math.round(overallHealth * 100))
  )
  // const wastageValues: number[] = insightUnits.map( i => i.effective_potential_efficiency_rate ).filter( e => e !== null )
  // const overallWastage = wastageValues.length ? sum(wastageValues) : null
  // const displayWastage = overallWastage === null ? null : (
  //   <Chip
  //     variant='outlined'
  //     size='small'
  //     classes={{
  //       root: classes.sectionToolbarChip,
  //       icon: classes.sectionToolbarChipIcon,
  //     }}
  //     icon={overallWastage === 0 ? <CheckOutlined color='inherit' /> : <WarningOutlined color='inherit' />}
  //     label={`Wastage ${percentageFormatter(overallWastage * 100)}`} />
  // )
  const IconComponent = collapsed ? ArrowDropUp : ArrowDropDown
  return (
    <div>
      <div
        className={classes.sectionToolbar}
        style={{
          backgroundColor,
          color: textColor,
        }}
        onClick={(): void => {
          if( insightUnits.length ){
            toggleCollapse()
          }
        }}>
        <IconComponent className={classes.sectionIcon} />
        <Typography variant='h5'>{ title }</Typography>
        { displayHealth && <span className={classes.sectionHealth}>{displayHealth}</span> }
        {/* { displayWastage && <span className={classes.sectionHealth}>{displayWastage}</span> } */}
        { explainer && (
          <div className={classes.sectionExplainer}>
            <SecondaryIconTooltip
              IconProps={{
                className: classes.sectionExplainerIcon,
              }}
              title={explainer} />
          </div>
        )}
      </div>
      <Collapse in={!collapsed}>
        <Box mt={4} mb={5}>
          <Grid container spacing={4}>
            { insightUnits.map( insightUnit => (
              <Grid key={insightUnit.id} item xs={12} md={6} lg={4} xl={3}>
                <InsightUnitComponent
                  insightUnit={insightUnit}
                  onClick={(): void => {
                    onInsightUnitClick(insightUnit)
                  }}
                  appTheme={appTheme}
                  classes={classes} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Collapse>
    </div>
  )
}


const orderedVerticals: InsightVertical[] = [
  'SEARCH', 'PERFORMANCE_MAX', 'MULTI_CHANNEL', 'VIDEO', 'DISPLAY'
]

const getOrderedVerticals = (verticals: InsightVertical[]): InsightVertical[] => {
  const matchedVerticals = intersection(orderedVerticals, verticals)
  const missingVerticals = xor(verticals, matchedVerticals)
  return [
    ...matchedVerticals,
    ...missingVerticals.sort(),
  ]
}

const getAvailableVerticals = (insightUnits: InsightOutputUnit[]): InsightVertical[] => (
  getOrderedVerticals(
    insightUnits.map( insight => insight.vertical ).filter( v => v !== null ),
  )
)

const getAllVerticals = (insightsByImportance: InsightUnitsByImportance): InsightVertical[] => (
  getOrderedVerticals(
    flatten(Object.values(insightsByImportance).map(getAvailableVerticals)),
  )
)

const getAvailableCategories = (insightUnits: InsightOutputUnit[]): InsightCategory[] => (
  getInsightCategoryOrder(
    insightUnits.map( insight => insight.category ).filter( v => v !== null ),
  )
)

const getAllCategories = (insightsByImportance: InsightUnitsByImportance): InsightCategory[] => (
  getInsightCategoryOrder(
    flatten(Object.values(insightsByImportance).map(getAvailableCategories)),
  )
)

const findInsightByDefinitionId = (
  insightsByImportance: InsightUnitsByImportance,
  definitionId: string
): InsightOutputUnit | null => {
  let match: InsightOutputUnit | null = null
  for( const insights of Object.values(insightsByImportance) ){
    match = insights.find( i => i.definition_id === definitionId ) || null
    if( match ){
      break
    }
  }
  return match
}

const orderedImportanceRatings: InsightImportance[] = [
  'CRITICAL',
  'KEY_TASK',
  'SECONDARY_TASK',
  'HOUSEKEEPING',
]

export const insightImportanceLabelMap: Record<InsightImportance, string> = {
  CRITICAL: 'Critical Alerts',
  KEY_TASK: 'Key Tasks',
  SECONDARY_TASK: 'Secondary Tasks',
  HOUSEKEEPING: 'Housekeeping',
}

const importanceExplainers: Record<InsightImportance, React.ReactNode> = {
  CRITICAL: (
    <>
      If any of these insights are not 100%, then there's a critical issue with your accounts that needs attention immediately
    </>
  ),
  KEY_TASK: (
    // <>
    //   Insights are sorted by a combination of health of the insight and the estimated potential wastage associated with the insight,
    //   so that the most important to deal with are always at the top of the section
    // </>
    <>
      Addressing these tasks is most likely to improve performance of campaigns.
    </>
  ),
  SECONDARY_TASK: (
    // <>
    //   These insights have a less direct effect on performance, but should be actioned to ensure best practice is followed
    // </>
    <>
      These tasks are still important and good to keep on top of to ensure good campaign management.
    </>
  ),
  HOUSEKEEPING: (
    // <>
    //   These insights might not have a direct impact on performance, but making sure that you keep your accounts well set up
    //   makes it easier to manage them, and saves time in the long run
    // </>
    <>
      Less important tasks, but good to tidy up when you can.
    </>
  )
}

const useStyles = makeAppStyles( theme => ({
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
  },
  titleContentRight: {
    marginLeft: 'auto',
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  secondaryTitleContent: {
    marginLeft: theme.spacing(2),
  },
  filterContainer: {
    marginLeft: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
  },
  categoryFilter: {
    marginLeft: theme.spacing(4),
  },
  filterControl: {
    marginRight: theme.spacing(2),
    '&:last-of-type': {
      marginRight: 0,
    }
  },
}))

export type InsightsByImportanceProps = (
  SeriesParams
  & Nullable<ReportParams>
  & Partial<Nullable<{ result_id: string }>>
  & {
    provider: ReportProviderV2
    insightsByImportance: InsightUnitsByImportance
    secondaryTitleContent?: React.ReactNode
    titleContentRight?: React.ReactNode
    enableFavourites?: boolean
    platformUnit?: PlatformUnit | null
  }
)

export const InsightsByImportance = ({
  series_id,
  report_id,
  result_id,
  provider,
  insightsByImportance,
  secondaryTitleContent,
  titleContentRight,
  enableFavourites,
  platformUnit,
}: InsightsByImportanceProps): JSX.Element => {

  const [state, setState] = useContext(InsightContext)

  const availableVerticals = getAllVerticals(insightsByImportance)

  const availableCategories = getAllCategories(insightsByImportance)

  const verticalFilters = state.verticals || []

  const categoryFilter = state.category || null

  const [report] = useReportSeriesReportPayload({
    series_id,
    report_id,
    ...(result_id && { result_id }),
  })

  const reportSeriesInsightUnitScores = useReportSeriesInsightUnitScores({ series_id })

  const matchingInsightScores = get(reportSeriesInsightUnitScores.data, report_id || '', null)

  const entity_type = get(report.data, 'root_entity_type', null)
  const entity_id = get(report.data, 'root_entity_id', null)

  const [reportEntity] = useReportSeriesReportEntity({
    series_id,
    report_id,
    entity_type,
    entity_id,
    ...(result_id && { result_id }),
  })

  // Load this eagerly so we're not waiting for metrics and example tables
  useFilteredMetricMetadata({
    provider: coerceReportProvider(provider) as ReportProvider,
  })

  useEffect(() => {
    const availableVerticals = getAllVerticals(insightsByImportance)
    if( state.verticals === undefined && availableVerticals.length ){
      setState({ verticals: availableVerticals })
    }else if( state.verticals && state.verticals.length && !availableVerticals.length ){
      setState({ verticals: undefined })
    }
  }, [insightsByImportance, setState, state.verticals])

  useEffect(() => {
    if( state.category ){
      const availableCategories = getAllCategories(insightsByImportance)
      if( !availableCategories.includes(state.category) ){
        setState({ category: undefined })
      }
    }
  }, [insightsByImportance, setState, state.category])

  const filteredInsightsByImportance = orderedImportanceRatings.reduce( (acc, importance) => {
    const filteredInsights = (insightsByImportance[importance] || []).filter( i => (
      (
        i.vertical === null || verticalFilters.includes(i.vertical)
      ) && (
        !categoryFilter || i.category === categoryFilter
      )
    ))
    acc[importance] = filteredInsights
    return acc
  }, {} as Record<InsightImportance, InsightOutputUnit[]>)

  const hasAnyLiveInsights = some(Object.values(insightsByImportance), i => !!i.length)

  const noInsightsMatchSelectedFilters = hasAnyLiveInsights && every(Object.values(filteredInsightsByImportance), i => i.length === 0 )

  const activeInsight = (
    state.insight ?
      findInsightByDefinitionId(insightsByImportance, state.insight) :
      null
  )

  const activeMetricId = state.metric || null
  const activeMetricSegment = state.segment
  const activeMetricDimension: DimensionType = state.dimension || 'count'

  const [collapsedSections, setCollapsedSections] = useState<Partial<Record<InsightImportance, boolean>>>({})

  const appTheme = useAppTheme()

  const classes = useStyles()

  const insightUnitClasses = useInsightUnitStyles()

  return (
    <>
      <div className={classes.flexCenter}>
        <ProviderLogo
          size={1.75}
          units='rem'
          provider={provider} />

        { secondaryTitleContent && (
          <div className={classes.secondaryTitleContent}>{secondaryTitleContent}</div>
        )}

        { !!availableVerticals.length && (
          <div className={classes.filterContainer}>
            { availableVerticals.map( vertical => {
              const isSelected = verticalFilters.includes(vertical)
              return (
                <RoundedPlainTextButton
                  key={vertical}
                  className={classes.filterControl}
                  variant='contained'
                  size='small'
                  color={isSelected ? 'secondary' : 'default'}
                  startIcon={
                    isSelected ? <Close /> : <Add />
                  }
                  onClick={(): void => {
                    // NOTE - we set verticals to null here when an update would
                    // mean we've deactivated them all, which is a valid view filter.
                    // This enables us to reserve 'undefined' for the detection of
                    // unset verticals, as opposed to exclusion which null signifies.
                    const updatedVerticals = xor(state.verticals, [vertical])
                    setState( prev => ({
                      ...prev,
                      verticals: updatedVerticals.length ? updatedVerticals : null
                    }))
                  }}>
                  { getInsightVerticalLabel(vertical) }
                </RoundedPlainTextButton>
              )
            })}
          </div>
        )}

        { !!availableCategories.length && (
          <RoundedPlainTextButtonMenu
            TriggerProps={{
              className: classes.categoryFilter,
              variant: 'contained',
              size: 'small',
              endIcon: <ArrowDropDown />,
            }}
            value={categoryFilter}
            label={
              categoryFilter ?
                <>
                  {getInsightCategoryLabel(categoryFilter)}
                  <Health
                    key={categoryFilter}
                    color='inherit'
                    animate={false}
                    className={classes.titleContentRight}
                    value={get(matchingInsightScores, categoryFilter, null)} />
                </>
                : 'All Categories'
            }
            options={[
              { label: 'All Categories', value: null },
              ...availableCategories.map( value => ({
                value,
                label: <>
                  {getInsightCategoryLabel(value)}
                  <Health
                    className={classes.titleContentRight}
                    animate={false}
                    value={get(matchingInsightScores, value, null)} />
                </>,
              })),
            ]}
            onChange={(e, category): void => {
              setState( prev => ({
                ...prev,
                category: category || undefined,
              }))
            }} />
        )}

        { titleContentRight && (
          <div className={classes.titleContentRight}>
            { titleContentRight }
          </div>
        )}
      </div>

      <Divider className={classes.divider} />

      { !hasAnyLiveInsights && (
        <Box p={6} display='flex' justifyContent='center'>
          <Typography variant='h4' color='textSecondary'>
            No activity detected
          </Typography>
        </Box>
      )}

      { noInsightsMatchSelectedFilters && (
        <Box p={6} display='flex' justifyContent='center'>
          <Typography variant='h4' color='textSecondary'>
            No insights match the selected filters
          </Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        { orderedImportanceRatings.map( importance => {
          const filteredInsights = filteredInsightsByImportance[importance]
          if( !filteredInsights.length ){
            return null
          }
          return (
            <Grid key={importance} item xs={12}>
              <CollapsibleInsightUnits
                appTheme={appTheme}
                classes={insightUnitClasses}
                title={insightImportanceLabelMap[importance]}
                explainer={importanceExplainers[importance]}
                insightUnits={filteredInsights}
                collapsed={filteredInsights.length === 0 || !!collapsedSections[importance]}
                toggleCollapse={(): void => {
                  setCollapsedSections( prev => ({
                    ...prev, [importance]: !prev[importance],
                  }))
                }}
                onInsightUnitClick={(insightUnit): void => {
                  setState({
                    insight: insightUnit.definition_id,
                  })
                }} />
            </Grid>
          )
        })}
      </Grid>

    { activeInsight && !reportEntity.data && (
      <BackdropLoader />
    )}

    { activeInsight && reportEntity.data && (
      <InsightOutputUnitDetail
        insightUnit={activeInsight}
        reportEntity={reportEntity.data}
        setActiveMetric={(metric): void => {
          setState({
            metric: metric.metric_id,
            segment: metric.segment && String(metric.segment.label),
          })
        }}
        provider={provider}
        series_id={series_id}
        platformUnit={platformUnit}
        enableFavourites={enableFavourites}
        DialogProps={{
          onClose: (): void => {
            setState({
              insight: undefined,
              metric: undefined,
              dimension: undefined,
              segment: undefined,
            })
          }
        }} />
    )}

    { activeMetricId && reportEntity.data && (
      <MetricDetailDialog
        provider={coerceReportProvider(provider) as ReportProvider}
        series_id={series_id}
        report_id={report_id}
        result_id={result_id}
        entity_type={entity_type}
        entity_id={entity_id}
        metric_id={activeMetricId}
        dimension={activeMetricDimension}
        segment={activeMetricSegment}
        onSegmentChange={(segment): void => {
          setState({
            segment,
          })
        }}
        onDimensionChange={(e, dimension): void => {
          setState({
            dimension: dimension as DimensionType,
          })
        }}
        reportMetric={reportEntity.data.metrics[activeMetricId]}
        platformUnit={platformUnit}
        enableFavourites={enableFavourites}
        DialogProps={{
          onClose: (): void => {
            setState({
              metric: undefined,
              dimension: undefined,
              segment: undefined,
            })
          }
        }} />
    )}

    </>
  )
}
